/**
 * Export one page: Markdown, HTML, PDF (the print dialog's "Save as PDF") or
 * Word. HTML, PDF and Word carry images inline, so the file stands on its own.
 */
import { generateHTML, type JSONContent } from '@tiptap/core';
import { isAttachmentTarget, schemaExtensions } from '@nexus/editor';
import type { Store } from '@nexus/store';
import { strToU8, zipSync } from 'fflate';
import { saveFile } from './export';

export type ExportFormat = 'md' | 'html' | 'pdf' | 'docx';

const safeName = (title: string) =>
  (title.trim() || 'Pagina').replace(/[\\/:*?"<>|]+/g, '-').slice(0, 120);

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function toBase64(data: Uint8Array): string {
  let s = '';
  for (let i = 0; i < data.length; i += 0x8000)
    s += String.fromCharCode(...data.subarray(i, i + 0x8000));
  return btoa(s);
}

/** `![[…]]` embeds become what a stand-alone file can show: inline images, or plain text. */
async function resolveEmbeds(store: Store, doc: JSONContent): Promise<JSONContent> {
  const walk = async (n: JSONContent): Promise<JSONContent> => {
    if (n.type === 'embed') {
      const target = String(n.attrs?.target ?? '');
      if (isAttachmentTarget(target)) {
        const info = await store.findAttachment(target);
        const blob = info && (await store.attachmentData(info.id));
        if (blob?.mime.startsWith('image/')) {
          const width = n.attrs?.width as number | null;
          return {
            type: 'paragraph',
            content: [
              {
                type: 'image',
                attrs: {
                  src: `data:${blob.mime};base64,${toBase64(blob.data)}`,
                  alt: target,
                  title: width ? `width:${width}` : null,
                },
              },
            ],
          };
        }
        return { type: 'paragraph', content: [{ type: 'text', text: `📎 ${target}` }] };
      }
      return { type: 'paragraph', content: [{ type: 'text', text: `↪ ${target}` }] };
    }
    return n.content ? { ...n, content: await Promise.all(n.content.map(walk)) } : n;
  };
  return walk(doc);
}

const CSS = `
body { font-family: Inter, 'Segoe UI', system-ui, sans-serif; color: #2b2420; line-height: 1.6;
  max-width: 46rem; margin: 2.5rem auto; padding: 0 1.5rem; }
h1.title { font-size: 2.2rem; margin: 0 0 1.5rem; letter-spacing: -0.02em; }
img { max-width: 100%; border-radius: 6px; }
table { border-collapse: collapse; width: 100%; margin: 1em 0; }
th, td { border: 1px solid #e3dcd2; padding: 0.35em 0.6em; vertical-align: top; text-align: left; }
th { background: #f7f4ef; }
pre { background: #f7f4ef; padding: 0.8em 1em; border-radius: 8px; overflow-x: auto; }
code { font-family: Consolas, Menlo, monospace; font-size: 0.9em; }
blockquote { border-left: 4px solid #ffe8dc; margin: 0; padding-left: 1em; color: #6b5d52; }
mark { background: rgba(255, 212, 0, 0.35); }
ul[data-type='taskList'] { list-style: none; padding-left: 0.2em; }
ul[data-type='taskList'] li { display: flex; gap: 0.5em; }
ul[data-type='taskList'] li[data-checked='true'] > div { text-decoration: line-through; color: #8a7c70; }
div[data-callout] { border: 1px solid #e3dcd2; background: #fbf8f3; border-radius: 10px; padding: 0.6em 0.9em; margin: 1em 0; }
.nx-columns { display: grid; grid-template-columns: repeat(var(--nx-cols, 2), minmax(0, 1fr)); gap: 1.5rem; }
@media print { body { margin: 0; max-width: none; } }
`;

/** The page as a complete HTML document. */
export async function pageToHtml(store: Store, title: string, doc: JSONContent): Promise<string> {
  const body = generateHTML(await resolveEmbeds(store, doc), schemaExtensions());
  return `<!doctype html>
<html lang="nl"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>${CSS}</style></head>
<body><h1 class="title">${escapeHtml(title)}</h1>
${body}
</body></html>`;
}

/**
 * A .docx that holds the page as HTML ("altChunk"): Word converts it on opening,
 * with headings, lists, tables and images.
 */
function htmlToDocx(html: string): Uint8Array {
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Default Extension="html" ContentType="text/html"/>
<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
    ),
    '_rels/.rels': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    ),
    'word/_rels/document.xml.rels': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="htmlChunk" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/aFChunk" Target="page.html"/>
</Relationships>`,
    ),
    'word/document.xml': strToU8(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<w:body><w:altChunk r:id="htmlChunk"/><w:sectPr/></w:body>
</w:document>`,
    ),
    'word/page.html': strToU8(html),
  };
  return zipSync(files, { level: 6 });
}

/** Print the HTML in a hidden frame: the dialog offers "Save as PDF". */
function printHtml(html: string) {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const win = frame.contentWindow!;
  win.document.open();
  win.document.write(html);
  win.document.close();
  const go = () => {
    win.focus();
    win.print();
    setTimeout(() => frame.remove(), 60_000);
  };
  // Wait for the images before printing.
  const images = [...win.document.images].filter((img) => !img.complete);
  if (!images.length) setTimeout(go, 50);
  else
    void Promise.all(images.map((img) => new Promise((r) => (img.onload = img.onerror = r)))).then(
      go,
    );
}

export async function exportPage(
  store: Store,
  format: ExportFormat,
  page: { title: string; doc: JSONContent; markdown: string },
): Promise<void> {
  const name = safeName(page.title);
  if (format === 'md') {
    await saveFile(`${name}.md`, strToU8(page.markdown), 'text/markdown', {
      name: 'Markdown',
      extensions: ['md'],
    });
    return;
  }
  const html = await pageToHtml(store, page.title, page.doc);
  if (format === 'html')
    await saveFile(`${name}.html`, strToU8(html), 'text/html', {
      name: 'HTML',
      extensions: ['html'],
    });
  else if (format === 'docx')
    await saveFile(
      `${name}.docx`,
      htmlToDocx(html),
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      { name: 'Word', extensions: ['docx'] },
    );
  else printHtml(html);
}
